import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import type { DeviceState } from './data/device-store';
import { useT } from './i18n';
import {
  approveWalletRecovery,
  loadIncomingRecoveryApprovals,
  type RecoveryCustodianRow,
  type RecoveryRequestRow,
  acceptRecoveryInvitation,
  createRecoveryDraft,
  currentAccountIdentity,
  declineRecoveryInvitation,
  ensureRecoveryHelperIdentity,
  loadRecoveryOverview,
  lookupRecoveryHelperProfile,
  sendRecoveryInvitation,
  verifyWalletPin,
  type RecoveryHelperProfile,
  type RecoveryInvitationRow,
  type RecoveryOverview,
} from './data/recovery-client';

function RecoveryShieldIcon({ success = false }: { success?: boolean }) {
  return <span className={`recovery-shield-icon ${success ? 'success' : ''}`} aria-hidden="true">
    <svg viewBox="0 0 24 24" focusable="false">
      <path d="M12 2.8 19 5.6v5.6c0 4.7-2.9 8.8-7 10-4.1-1.2-7-5.3-7-10V5.6L12 2.8Z" />
      {success
        ? <path className="recovery-shield-mark" d="m8.4 12.1 2.2 2.2 4.8-5" />
        : <path className="recovery-shield-mark" d="M12 8.2v4.2m0 3.2h.01" />}
    </svg>
  </span>;
}

function PinConfirmationDialog({
  device,
  vaultRootKey,
  title,
  description,
  subject,
  confirmLabel,
  onClose,
  onConfirmed,
}: {
  device: DeviceState;
  vaultRootKey: Uint8Array;
  title: string;
  description: string;
  subject?: string;
  confirmLabel: string;
  onClose: () => void;
  onConfirmed: () => Promise<void>;
}) {
  const { t } = useT();
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await verifyWalletPin({ device, pin, vaultRootKey });
      await onConfirmed();
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('invite.errConfirm'));
    } finally {
      setBusy(false);
    }
  }

  return <div className="overlay recovery-overlay" role="presentation" onMouseDown={() => { if (!busy) onClose(); }}>
    <section className="sheet recovery-action-sheet" role="dialog" aria-modal="true" aria-labelledby="recovery-pin-title" onMouseDown={(event) => event.stopPropagation()}>
      <div className="recovery-dialog-hero">
        <RecoveryShieldIcon />
        <div><p className="eyebrow">{t('invite.securityConfirmation')}</p><h2 id="recovery-pin-title">{title}</h2></div>
      </div>
      <p className="recovery-dialog-copy">{description}</p>
      {subject && <div className="recovery-person-chip"><span aria-hidden="true">@</span><strong>{subject}</strong></div>}
      <form className="recovery-pin-form" onSubmit={submit}>
        <label>{t('account.pinLabel')}
          <input
            autoFocus
            className="concealed-input recovery-pin-input"
            inputMode="numeric"
            autoComplete="off"
            value={pin}
            onChange={(event) => setPin(event.target.value.replace(/\D/g, '').slice(0, 12))}
            placeholder={t('invite.enterPin')}
          />
        </label>
        {error && <p className="form-error recovery-dialog-error" role="alert">{error}</p>}
        <div className="recovery-dialog-actions">
          <button className="secondary-button" type="button" disabled={busy} onClick={onClose}>{t('action.cancel')}</button>
          <button className="primary-button" type="submit" disabled={busy || pin.length < 6}>{busy ? t('invite.confirming') : confirmLabel}</button>
        </div>
      </form>
    </section>
  </div>;
}

function InvitationReviewDialog({
  invitation,
  device,
  vaultRootKey,
  startOnDecline = false,
  onClose,
  onChanged,
}: {
  invitation: RecoveryInvitationRow;
  device: DeviceState;
  vaultRootKey: Uint8Array;
  startOnDecline?: boolean;
  onClose: () => void;
  onChanged: () => Promise<void>;
}) {
  const { t } = useT();
  const [step, setStep] = useState<'review' | 'pin' | 'decline' | 'success'>(startOnDecline ? 'decline' : 'review');
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function accept(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await verifyWalletPin({ device, pin, vaultRootKey });
      await acceptRecoveryInvitation({ invitation, device, vaultRootKey });
      await onChanged();
      setStep('success');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('invite.errAccept'));
    } finally {
      setBusy(false);
    }
  }

  async function decline() {
    setBusy(true);
    setError('');
    try {
      await declineRecoveryInvitation(invitation.id);
      await onChanged();
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('invite.errDecline'));
    } finally {
      setBusy(false);
    }
  }

  return <div className="overlay recovery-overlay" role="presentation" onMouseDown={() => { if (!busy) onClose(); }}>
    <section className="sheet recovery-invitation-sheet" role="dialog" aria-modal="true" aria-labelledby="recovery-invitation-title" onMouseDown={(event) => event.stopPropagation()}>
      {step === 'review' && <>
        <div className="recovery-dialog-hero">
          <RecoveryShieldIcon />
          <div><p className="eyebrow">{t('invite.title')}</p><h2 id="recovery-invitation-title">{t('invite.helpSomeone')}</h2></div>
        </div>
        <div className="recovery-inviter-card">
          <span className="recovery-avatar" aria-hidden="true">{invitation.owner_email.slice(0, 1).toUpperCase()}</span>
          <div><small>{t('invite.invitedBy')}</small><strong>{invitation.owner_email}</strong></div>
        </div>
        <p className="recovery-dialog-copy">{t('invite.askedYou')}</p>
        <div className="recovery-promise-list">
          <div><span aria-hidden="true">✓</span><p><strong>{t('invite.cannotSee')}</strong><small>{t('invite.cannotSeeNote')}</small></p></div>
          <div><span aria-hidden="true">✓</span><p><strong>{t('invite.cannotRecover')}</strong><small>{t('invite.cannotRecoverNote')}</small></p></div>
          <div><span aria-hidden="true">✓</span><p><strong>{t('invite.nothingToRemember')}</strong><small>{t('invite.nothingToRememberNote')}</small></p></div>
        </div>
        {error && <p className="form-error recovery-dialog-error" role="alert">{error}</p>}
        <div className="recovery-dialog-actions stacked-mobile">
          <button className="secondary-button" type="button" disabled={busy} onClick={() => setStep('decline')}>{t('invite.decline')}</button>
          <button className="primary-button" type="button" disabled={busy} onClick={() => setStep('pin')}>{t('invite.accept')}</button>
        </div>
      </>}

      {step === 'pin' && <>
        <div className="recovery-dialog-hero">
          <RecoveryShieldIcon />
          <div><p className="eyebrow">{t('invite.confirmWithPin')}</p><h2 id="recovery-invitation-title">{t('invite.acceptTitle')}</h2></div>
        </div>
        <p className="recovery-dialog-copy">{t('invite.confirmBecome')}</p>
        <div className="recovery-person-chip"><span aria-hidden="true">@</span><strong>{invitation.owner_email}</strong></div>
        <form className="recovery-pin-form" onSubmit={accept}>
          <label>{t('account.pinLabel')}
            <input
              autoFocus
              className="concealed-input recovery-pin-input"
              inputMode="numeric"
              autoComplete="off"
              value={pin}
              onChange={(event) => setPin(event.target.value.replace(/\D/g, '').slice(0, 12))}
              placeholder={t('invite.enterPin')}
            />
          </label>
          {error && <p className="form-error recovery-dialog-error" role="alert">{error}</p>}
          <div className="recovery-dialog-actions">
            <button className="secondary-button" type="button" disabled={busy} onClick={() => { setError(''); setStep('review'); }}>Back</button>
            <button className="primary-button" type="submit" disabled={busy || pin.length < 6}>{busy ? 'Accepting…' : 'Accept invitation'}</button>
          </div>
        </form>
      </>}

      {step === 'decline' && <>
        <div className="recovery-dialog-hero danger">
          <span className="recovery-decline-icon" aria-hidden="true">×</span>
          <div><p className="eyebrow">{t('invite.declineTitle')}</p><h2 id="recovery-invitation-title">{t('invite.declineQuestion')}</h2></div>
        </div>
        <p className="recovery-dialog-copy">{t('invite.invitationFrom')} <strong>{invitation.owner_email}</strong> {t('invite.willBeRemoved')}</p>
        {error && <p className="form-error recovery-dialog-error" role="alert">{error}</p>}
        <div className="recovery-dialog-actions">
          <button className="secondary-button" type="button" disabled={busy} onClick={() => setStep('review')}>{t('invite.keep')}</button>
          <button className="danger-button recovery-decline-button" type="button" disabled={busy} onClick={() => void decline()}>{busy ? 'Declining…' : 'Decline invitation'}</button>
        </div>
      </>}

      {step === 'success' && <>
        <div className="recovery-success-state">
          <RecoveryShieldIcon success />
          <p className="eyebrow">{t('invite.accepted')}</p>
          <h2 id="recovery-invitation-title">{t('invite.nowContact')}</h2>
          <p>{t('invite.nothingUnless')} <strong>{invitation.owner_email}</strong> {t('invite.asksForHelp')}</p>
        </div>
        <button className="primary-button recovery-done-button" type="button" onClick={onClose}>Done</button>
      </>}
    </section>
  </div>;
}

/**
 * Approving someone's recovery.
 *
 * This is the only step that actually hands a wallet back, so the phone call is the real
 * control here, not the waiting period: the code below is shown so the contact can read it
 * aloud and hear the same one read back. The warning is therefore given the weight of the
 * screen rather than being a footnote under the buttons.
 *
 * There is deliberately no "reject" that writes to the server. A contact who is suspicious
 * should tell the owner, who cancels it — that keeps the decision with the person whose
 * wallet it is.
 */
function RecoveryApprovalDialog({ request, contact, device, vaultRootKey, onClose, onApproved }: {
  request: RecoveryRequestRow;
  contact: RecoveryCustodianRow;
  device: DeviceState;
  vaultRootKey: Uint8Array;
  onClose: () => void;
  onApproved: () => Promise<void>;
}) {
  const { t } = useT();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  async function approve() {
    setBusy(true);
    setError('');
    try {
      await approveWalletRecovery({ request, contact, device, vaultRootKey });
      setDone(true);
      await onApproved();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('approve.errApprove'));
    } finally {
      setBusy(false);
    }
  }

  return <div className="overlay recovery-overlay" role="presentation" onMouseDown={() => { if (!busy) onClose(); }}>
    <section className="sheet recovery-action-sheet" role="dialog" aria-modal="true" aria-labelledby="recovery-approve-title" onMouseDown={(event) => event.stopPropagation()}>
      <div className="recovery-dialog-hero">
        <RecoveryShieldIcon success={done} />
        <div>
          <p className="eyebrow">{t('approve.inboxTitle')}</p>
          <h2 id="recovery-approve-title">{done ? t('approve.done') : t('approve.title')}</h2>
        </div>
      </div>

      {done ? <div className="recovery-dialog-actions">
        <button className="primary-button" type="button" onClick={onClose}>{t('recover.continue')}</button>
      </div> : <>
        <div className="recovery-person-chip"><span aria-hidden="true">@</span><strong>{request.owner_email}</strong></div>

        <p className="eyebrow">{t('approve.fingerprintLabel')}</p>
        <p className="pairing-fingerprint">{request.recovering_fingerprint}</p>

        <p className="pairing-warning"><strong>{t('approve.callFirst')}</strong></p>
        <p className="recovery-dialog-copy">{t('approve.onlyIfSure')}</p>

        {error && <p className="form-error recovery-dialog-error" role="alert">{error}</p>}
        <div className="recovery-dialog-actions stacked-mobile">
          <button className="secondary-button" type="button" disabled={busy} onClick={onClose}>{t('approve.notNow')}</button>
          <button className="primary-button" type="button" disabled={busy} onClick={() => void approve()}>
            {busy ? t('approve.approving') : t('approve.approve')}
          </button>
        </div>
      </>}
    </section>
  </div>;
}

/** Recovery requests waiting on this person, shown on Home beside recovery invitations. */
export function RecoveryApprovalInbox({ device, vaultRootKey }: {
  device: DeviceState;
  vaultRootKey: Uint8Array;
}) {
  const { t } = useT();
  const [pending, setPending] = useState<Array<{ request: RecoveryRequestRow; contact: RecoveryCustodianRow }>>([]);
  const [selected, setSelected] = useState<{ request: RecoveryRequestRow; contact: RecoveryCustodianRow } | null>(null);

  const refresh = useCallback(async () => {
    if (!device.accountUserId) return;
    try {
      setPending(await loadIncomingRecoveryApprovals());
    } catch {
      // Recovery stays out of the way on Home when cloud setup is unavailable.
    }
  }, [device.accountUserId]);

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => void refresh(), 10_000);
    return () => window.clearInterval(timer);
  }, [refresh]);

  if (pending.length === 0) return null;

  return <section className="content-section recovery-inbox-section" aria-labelledby="recovery-approve-heading">
    <div className="section-heading"><h2 id="recovery-approve-heading">{t('approve.inboxTitle')}</h2></div>
    <div className="recovery-inbox-list">
      {pending.map(({ request, contact }) => <article className="recovery-inbox-card" key={request.id}>
        <RecoveryShieldIcon />
        <div className="recovery-inbox-copy">
          <strong>{request.owner_email}</strong>
          <p>{t('approve.isRecovering')}</p>
        </div>
        <div className="recovery-inbox-actions">
          <button className="secondary-button compact" type="button" onClick={() => setSelected({ request, contact })}>{t('approve.review')}</button>
        </div>
      </article>)}
    </div>
    {selected && <RecoveryApprovalDialog
      request={selected.request}
      contact={selected.contact}
      device={device}
      vaultRootKey={vaultRootKey}
      onClose={() => setSelected(null)}
      onApproved={refresh}
    />}
  </section>;
}

export function RecoveryInvitationInbox({ device, vaultRootKey }: {
  device: DeviceState;
  vaultRootKey: Uint8Array;
}) {
  const { t } = useT();
  const [account, setAccount] = useState<{ userId: string; email: string } | null>(null);
  const [overview, setOverview] = useState<RecoveryOverview | null>(null);
  const [selected, setSelected] = useState<{ invitation: RecoveryInvitationRow; decline: boolean } | null>(null);

  const refresh = useCallback(async () => {
    if (!device.accountUserId) return;
    try {
      await ensureRecoveryHelperIdentity({ device, vaultRootKey });
      const identity = await currentAccountIdentity();
      const data = await loadRecoveryOverview(device.vaultId);
      setAccount(identity);
      setOverview(data);
    } catch {
      // Recovery stays out of the way on Home when cloud setup is unavailable.
    }
  }, [device, vaultRootKey]);

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => void refresh(), 10_000);
    return () => window.clearInterval(timer);
  }, [refresh]);

  const invitations = useMemo(() => {
    if (!overview || !account) return [];
    return overview.invitations.filter((invitation) =>
      invitation.state === 'pending'
      && invitation.owner_user_id !== account.userId
      && (invitation.invited_user_id === account.userId || invitation.invited_email.toLowerCase() === account.email.toLowerCase()),
    );
  }, [account, overview]);

  if (invitations.length === 0) return null;

  return <section className="content-section recovery-inbox-section" aria-labelledby="recovery-inbox-heading">
    <div className="section-heading"><h2 id="recovery-inbox-heading">{t('invite.inboxTitle')}</h2></div>
    <div className="recovery-inbox-list">
      {invitations.map((invitation) => <article className="recovery-inbox-card" key={invitation.id}>
        <RecoveryShieldIcon />
        <div className="recovery-inbox-copy">
          <strong>{invitation.owner_email}</strong>
          <p>{t('invite.invitedYouTo')}</p>
        </div>
        <div className="recovery-inbox-actions">
          <button className="quiet-link" type="button" onClick={() => setSelected({ invitation, decline: true })}>{t('invite.decline')}</button>
          <button className="secondary-button compact" type="button" onClick={() => setSelected({ invitation, decline: false })}>{t('invite.review')}</button>
        </div>
      </article>)}
    </div>
    {selected && <InvitationReviewDialog
      invitation={selected.invitation}
      device={device}
      vaultRootKey={vaultRootKey}
      startOnDecline={selected.decline}
      onClose={() => setSelected(null)}
      onChanged={refresh}
    />}
  </section>;
}

export function RecoveryProtectionPanel({ device, vaultRootKey }: {
  device: DeviceState;
  vaultRootKey: Uint8Array;
}) {
  const { t, plural } = useT();
  const [overview, setOverview] = useState<RecoveryOverview | null>(null);
  const [account, setAccount] = useState<{ userId: string; email: string } | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [helper, setHelper] = useState<RecoveryHelperProfile | null>(null);
  const [pinOpen, setPinOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    try {
      await ensureRecoveryHelperIdentity({ device, vaultRootKey });
      const [identity, data] = await Promise.all([
        currentAccountIdentity(),
        loadRecoveryOverview(device.vaultId),
      ]);
      setAccount(identity);
      setOverview(data);
      setError('');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('protect.errLoad'));
    }
  }, [device, vaultRootKey]);

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => void refresh(), 10_000);
    return () => window.clearInterval(timer);
  }, [refresh]);

  const activeSet = overview?.sets.find((item) => item.state === 'active') ?? null;
  const draftSet = overview?.sets.find((item) => item.state === 'draft') ?? null;
  const currentSet = activeSet ?? draftSet;
  const pendingInvitations = overview?.invitations.filter((invitation) =>
    invitation.vault_id === device.vaultId
    && invitation.recovery_set_id === currentSet?.id
    && invitation.state === 'pending',
  ) ?? [];
  const activeContacts = overview?.custodians.filter((contact) =>
    contact.vault_id === device.vaultId
    && contact.recovery_set_id === currentSet?.id
    && contact.state === 'active',
  ) ?? [];

  async function startInvitation(event: FormEvent) {
    event.preventDefault();
    const normalized = inviteEmail.trim().toLowerCase();
    setMessage('');
    setError('');
    if (!normalized) return;
    if (account && normalized === account.email.toLowerCase()) {
      setError(t('protect.errSelf'));
      return;
    }
    setBusy(true);
    try {
      const profile = await lookupRecoveryHelperProfile(normalized);
      if (!profile) {
        throw new Error(t('protect.errNoAccount'));
      }
      if (!profile.publicKey) {
        throw new Error(t('protect.errNotReady'));
      }
      setHelper({ ...profile, publicKey: profile.publicKey });
      setPinOpen(true);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('protect.errCheck'));
    } finally {
      setBusy(false);
    }
  }

  async function confirmInvitation() {
    if (!helper) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const recoverySetId = currentSet?.id ?? await createRecoveryDraft({ device, vaultRootKey });
      await sendRecoveryInvitation({ device, vaultRootKey, recoverySetId, helper });
      setInviteEmail('');
      setHelper(null);
      setMessage(`Invitation sent. Waiting for ${helper.email} to accept.`);
      await refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('protect.errSend'));
      await refresh().catch(() => undefined);
      throw cause;
    } finally {
      setBusy(false);
    }
  }

  if (!account && !error) return <div className="recovery-panel recovery-panel-loading"><p>{t('protect.preparing')}</p></div>;

  const status = activeContacts.length > 0
    ? { tone: 'active', label: t('protect.protectionActive'), detail: plural('helpers', activeContacts.length) }
    : pendingInvitations.length > 0
      ? { tone: 'pending', label: t('protect.waiting'), detail: t('protect.becomesActive') }
      : { tone: 'warning', label: t('protect.notSetUp'), detail: t('protect.addSoYouCan') };

  return <div className="recovery-panel redesigned-recovery-panel">
    <div className={`recovery-overview-card ${status.tone}`}>
      <RecoveryShieldIcon success={status.tone === 'active'} />
      <div><strong>{status.label}</strong><p>{status.detail}</p></div>
    </div>

    <section className="recovery-add-card" aria-labelledby="add-recovery-contact-heading">
      <div className="recovery-add-heading">
        <span className="recovery-step-number">1</span>
        <div><h4 id="add-recovery-contact-heading">{t('protect.addPerson')}</h4><p>{t('protect.addPersonNote')}</p></div>
      </div>
      <div className="recovery-account-requirement"><span aria-hidden="true">i</span><p><strong>{t('protect.mustHaveAccount')}</strong><small>{t('protect.useTheirEmail')}</small></p></div>
      <form className="recovery-invite-form" onSubmit={startInvitation}>
        <label>{t('protect.emailLabel')}
          <input type="email" autoComplete="email" placeholder={t('protect.emailPlaceholder')} value={inviteEmail} onChange={(event) => setInviteEmail(event.target.value)} />
        </label>
        <button className="primary-button" type="submit" disabled={busy || !inviteEmail.trim()}>{busy ? t('protect.checking') : t('protect.send')}</button>
      </form>
    </section>

    {(pendingInvitations.length > 0 || activeContacts.length > 0) && <section className="recovery-owner-list" aria-labelledby="trusted-people-heading">
      <div className="recovery-owner-list-heading"><h4 id="trusted-people-heading">{t('protect.yourPeople')}</h4><small>{t('protect.onlyYouSee')}</small></div>
      {pendingInvitations.map((invitation) => <div className="recovery-owner-row" key={invitation.id}>
        <span className="recovery-avatar" aria-hidden="true">{invitation.invited_email.slice(0, 1).toUpperCase()}</span>
        <div><strong>{invitation.invited_email}</strong><small>{t('protect.waiting')}</small></div>
        <span className="recovery-contact-badge pending">{t('protect.pending')}</span>
      </div>)}
      {activeContacts.map((contact) => <div className="recovery-owner-row" key={contact.id}>
        <span className="recovery-avatar active" aria-hidden="true">{contact.custodian_email.slice(0, 1).toUpperCase()}</span>
        <div><strong>{contact.custodian_email}</strong><small>{t('protect.canHelp')}</small></div>
        <span className="recovery-contact-badge active">{t('protect.active')}</span>
      </div>)}
    </section>}

    {message && <p className="status-message recovery-status-message" role="status">{message}</p>}
    {error && <p className="form-error recovery-panel-error" role="alert">{error}</p>}

    {pinOpen && helper && <PinConfirmationDialog
      device={device}
      vaultRootKey={vaultRootKey}
      title={t('invite.enterWalletPin')}
      description={t('invite.confirmInviteNote')}
      subject={helper.email}
      confirmLabel={t('invite.confirmInvite')}
      onClose={() => { setPinOpen(false); setHelper(null); }}
      onConfirmed={confirmInvitation}
    />}
  </div>;
}
